import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/useAuthStore'
import { useI18n } from '../i18n'
import { UsernameField } from '../components/UsernameField'

type Tab = 'login' | 'register'

interface Props {
  initialTab?: Tab
}

export function LoginPage({ initialTab = 'login' }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const register = useAuthStore((s) => s.register)
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle)
  const error = useAuthStore((s) => s.error)
  const clearError = useAuthStore((s) => s.clearError)

  const [tab, setTab] = useState<Tab>(initialTab)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [username, setUsername] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const switchTab = (t: Tab) => {
    setTab(t)
    clearError()
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const ok = await login(email, password)
      if (ok) navigate('/')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password !== confirmPassword) return
    setIsSubmitting(true)
    try {
      const ok = await register(username, email, password)
      if (ok) navigate('/')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Tab Switcher */}
        <div className="mb-6 flex rounded-lg bg-surface-light p-1">
          <button
            onClick={() => switchTab('login')}
            className={`flex-1 rounded-md py-2.5 text-sm font-medium transition-colors ${
              tab === 'login'
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text'
            }`}
          >
            {t('auth.login')}
          </button>
          <button
            onClick={() => switchTab('register')}
            className={`flex-1 rounded-md py-2.5 text-sm font-medium transition-colors ${
              tab === 'register'
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text'
            }`}
          >
            {t('auth.register')}
          </button>
        </div>

        {/* Google Login */}
        <button
          onClick={loginWithGoogle}
          className="mb-4 flex w-full items-center justify-center gap-3 rounded-lg border-2 border-surface-lighter bg-surface-light py-3 font-medium text-text transition-colors hover:border-primary"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          {t('auth.loginWithGoogle')}
        </button>

        {/* Divider */}
        <div className="mb-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-surface-lighter" />
          <span className="text-xs text-text-muted">{t('auth.orDivider')}</span>
          <div className="h-px flex-1 bg-surface-lighter" />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-lg bg-error/10 p-3 text-sm text-error">
            {error}
          </div>
        )}

        {/* Login Form */}
        {tab === 'login' && (
          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <input
              type="email"
              placeholder={t('auth.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            <input
              type="password"
              placeholder={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-primary py-3 font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
            >
              {t('auth.login')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-text-muted hover:text-primary transition-colors"
            >
              {t('auth.forgotPassword')}
            </button>
            <p className="text-center text-sm text-text-muted">
              {t('auth.noAccount')}{' '}
              <button
                type="button"
                onClick={() => switchTab('register')}
                className="text-primary hover:underline"
              >
                {t('auth.register')}
              </button>
            </p>
          </form>
        )}

        {/* Register Form */}
        {tab === 'register' && (
          <form onSubmit={handleRegister} className="flex flex-col gap-4">
            <UsernameField value={username} onChange={setUsername} />
            <input
              type="email"
              placeholder={t('auth.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            <input
              type="password"
              placeholder={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            <input
              type="password"
              placeholder={t('auth.confirmPassword')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="rounded-lg border-2 border-surface-lighter bg-surface-light px-4 py-3 text-text outline-none transition-colors focus:border-primary"
            />
            {password && confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-error">{t('auth.passwordMismatch')}</p>
            )}
            <button
              type="submit"
              disabled={isSubmitting || password !== confirmPassword}
              className="rounded-lg bg-primary py-3 font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
            >
              {t('auth.register')}
            </button>
            <p className="text-center text-sm text-text-muted">
              {t('auth.hasAccount')}{' '}
              <button
                type="button"
                onClick={() => switchTab('login')}
                className="text-primary hover:underline"
              >
                {t('auth.login')}
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
